import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { getApi, postApi } from "../../utils/api";
import {
  UPLOAD_USER_PHOTO,
  USER_PROFILE,
  API_GET_EMPLOYEE_FILE,
  API_MY_INSURANCE,
  API_PHASE2_LIST,
  API_MY_SQE_SHIFT_TIMING,
} from "../../config/Endpoints";
import Cookies from "js-cookie";
import { showSuccess, showError, showInfo, showWarning } from "../../v2/utils/toast.ts";
import { TailSpin, ThreeDots } from "react-loader-spinner";
import Salary from "./Salary";
import Form16 from "./Form16";
import WorkExperience from "./WorkExperience";
import EmployeeAsset from "./EmployeeAsset";
import OrgChartExample from "../OrgChartExample";
import IndexChart from "../org-diagram";
import Education from "./Education";
import Dependents from "./Dependent";
import { logoutUser } from "../../utils/Storage";
import ChangePassword from "./ChangePassword";
import {
  displayFormatDate,
  formatDate,
  formatCalendarDateDisplay,
  formatToDateOnly,
  getEmployeePhone,
  getFirstDefinedField,
  toggleMenu,
} from "../../utils/helpers";
import DocumentList from "./DocumentList";
import AppraisalList from "./AppraisalList";
import Footer from "../../components/Footer";
import { Event } from "../../config/events";
import axios from "axios";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { employeeTypeOptions } from "../../options/employeeTypeOptions";
import { ProfileHeaderCard } from "../../v2/components/features/my-profile/components/profile-header-card.tsx";
import { useSqeLabs } from "../../v2/hooks/use-sqe-labs.ts";
import { isSQEEmployeeType } from "../../v2/utils/sqe-labs-navigation-access.ts";
import { SqeShiftTiming } from "../../v2/components/features/sqe-shift-timing/sqe-shift-timing.tsx";
import ProfileBankDetails from "./ProfileBankDetails";
import ProfileInsuranceDetails from "./ProfileInsuranceDetails";
import { EmployeeTechStack } from "../../v2/components/features/admin-profile/tech-stack/index.ts";
import { EmployeeCertifications } from "../../v2/components/features/admin-profile/certifications/index.ts";
import { useIsUsEmployee } from "../../v2/hooks/use-is-us-employee.ts";
import { useCompanyNameOptions } from "../../v2/hooks/use-company-name-options.ts";
import { isInsuranceEligibleLocation } from "../../v2/utils/insurance-eligibility.ts";

function getLeaveManagerDisplayName(personalDetail, userData) {
  const name =
    personalDetail?.leaveManagerName ??
    userData?.leaveManagerName ??
    "";
  return name && String(name).trim() ? name : "-";
}

const AdminProfile = () => {
  const isUsEmployee = useIsUsEmployee();
  const companyNames = useCompanyNameOptions();
  const [userData, setUserData] = useState({});
  const accessToken = Cookies.get("accessToken");
  const [showBankDetailsTab, setShowBankDetailsTab] = useState(false);
  const [insuranceDetails, setInsuranceDetails] = useState(null);
  const [profileImage, setProfileImage] = useState("../assets/profile.png");
  const [croppedBlob, setCroppedBlob] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [minZoom, setMinZoom] = useState(1);
  const [showCropModal, setShowCropModal] = useState(false);
  const [upImg, setUpImg] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const isSqeLabs = useSqeLabs();
  const profileEmail = isSqeLabs
    ? userData?.personalDetail?.primaryEmail || "-"
    : userData?.personalDetail?.ccEmail || "-";
  const employeePhone = getEmployeePhone(
    userData?.personalDetail?.phoneNumber,
    userData?.personalDetail?.location,
  );
  const showInsuranceDetailsTab = isInsuranceEligibleLocation(
    userData?.personalDetail?.location,
    userData?.personalDetail?.employeeType ?? userData?.personalDetail?.empType,
  );
  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };
  const onMediaLoaded = (mediaSize) => {
    const { width, height } = mediaSize;
    const cropSize = 200; // cropSize width/height
    // Calculate minimum zoom to fill the crop area
    const minZoomValue = Math.max(cropSize / width, cropSize / height);
    setMinZoom(minZoomValue);
  };

  useEffect(() => {
    if (showCropModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showCropModal]);

  const handleCropSave = async () => {
    setIsUploading(true);
    let blobToUpload = croppedBlob;

    try {
      // If not in preview mode, create blob from current crop
      if (!blobToUpload && upImg && croppedAreaPixels) {
        const image = await createImage(upImg);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;

        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
        );

        blobToUpload = await new Promise((resolve) => {
          canvas.toBlob(resolve, "image/jpeg", 0.95);
        });
      }

      if (!blobToUpload) {
        showError("Failed to process image.");
        setIsUploading(false);
        return;
      }

      const formData = new FormData();
      const file = new File([blobToUpload], "profile.jpg", {
        type: "image/jpeg",
      });
      formData.append("photo", file);

      const res = await postApi(UPLOAD_USER_PHOTO, formData, accessToken);
      if (res.status === 200) {
        showSuccess(`${res.data.message}` || "Profile Updated Successfully!");

        // SUCCESS: Close modal and refresh
        setShowCropModal(false);
        setIsPreviewMode(false);
        const imageUrl = URL.createObjectURL(blobToUpload);
        setProfileImage(imageUrl);
        setCroppedBlob(null);
        setIsUploading(false);

        window.dispatchEvent(new CustomEvent(Event.USER.PROFILE_IMAGE_UPDATED));
        fetchUser(false); // refresh user details
      } else {
        setIsUploading(false);
        showError(`Failed to update photo. ${res.data.message}`);
      }
    } catch (e) {
      console.error("Handle crop save error:", e);
      setIsUploading(false);
      showError("Failed to update photo.");
    }
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        showError("Only JPG, JPEG, or PNG files are allowed.");
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        showError("Photo size exceeds the maximum limit of 2MB.");
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          // Image loaded successfully
          setUpImg(reader.result);
          setShowCropModal(true);
        };
        img.onerror = () => {
          // Image is corrupted or invalid
          showError("The selected photo appears to be corrupted or invalid.");
        };
        img.src = reader.result;
      };

      reader.readAsDataURL(file);

      setPreviewImage(null);
      setCroppedBlob(null);
      setIsPreviewMode(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setMinZoom(1);
      setCroppedAreaPixels(null);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current.value = null;
    setUpImg(null);
    setPreviewImage(null);
    setCroppedBlob(null);
    setIsPreviewMode(false);
    fileInputRef.current.click();
  };

  const handlePreviewCrop = async () => {
    if (!upImg || !croppedAreaPixels) return;

    const image = await createImage(upImg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
    );

    const base64Image = canvas.toDataURL("image/jpeg");
    setPreviewImage(base64Image);

    canvas.toBlob(
      (blob) => {
        setCroppedBlob(blob);
      },
      "image/jpeg",
      0.95,
    );

    setIsPreviewMode(true);
  };

  useEffect(() => {
    if (accessToken) {
      fetchUser();
    }
  }, []);

  useEffect(() => {
    const employeeId = userData?.personalDetail?.id;
    if (!employeeId || !accessToken) {
      setShowBankDetailsTab(false);
      setInsuranceDetails(null);
      return;
    }

    getApi(
      `${API_PHASE2_LIST}?employeeId=${encodeURIComponent(
        String(employeeId),
      )}&currentPage=1&pageSize=100`,
      accessToken,
    )
      .then((res) => {
        if (res.status === 200) {
          const payload = res?.data?.data ?? res?.data;
          const content =
            payload?.content ??
            payload?.data ??
            (Array.isArray(payload) ? payload : null);
          const list = Array.isArray(content) ? content : [];
          const insuranceRow = list.find(
            (item) =>
              Number(item?.employee?.id ?? item?.employeeId) ===
              Number(employeeId),
          );
          setShowBankDetailsTab(insuranceRow?.bankDetails != null);
        } else {
          setShowBankDetailsTab(false);
        }
      })
      .catch(() => {
        setShowBankDetailsTab(false);
      });

    if (!showInsuranceDetailsTab) {
      setInsuranceDetails(null);
      return;
    }

    getApi(
      `${API_MY_INSURANCE}/${encodeURIComponent(String(employeeId))}`,
      accessToken,
    )
      .then((res) => {
        const payload = res?.data?.data ?? res?.data;
        setInsuranceDetails(payload ?? null);
      })
      .catch(() => {
        setInsuranceDetails(null);
      });
  }, [
    userData?.personalDetail?.id,
    userData?.personalDetail?.maritalStatus,
    accessToken,
    showInsuranceDetailsTab,
  ]);

  const fetchUser = (showGlobalLoader = true) => {
    if (showGlobalLoader) setIsLoading(true);

    getApi(USER_PROFILE, accessToken)
      .then(async (res) => {
        if (res.status === 200) {
          const user = res.data.data;
          setUserData(user);
          setIsLoading(false);

          const photoPath = user?.personalDetail?.photoPath;
          setIsImageLoading(true);

          if (photoPath?.includes("photoDir")) {
            const parts = photoPath.split("/");
            const result = parts.slice(3).join("/");

            try {
              const imgRes = await axios.get(API_GET_EMPLOYEE_FILE, {
                headers: { Authorization: `Bearer ${accessToken}` },
                params: { filepath: result, t: new Date().getTime() },
                responseType: "blob",
              });

              if (imgRes.status === 200) {
                const imageUrl = URL.createObjectURL(imgRes.data);
                setProfileImage(imageUrl);
              } else {
                setProfileImage("../assets/profile.png");
              }
            } catch (err) {
              console.error("Error fetching image:", err);
              setProfileImage("../assets/profile.png");
            }
          } else {
            setProfileImage("../assets/profile.png");
          }
          setIsImageLoading(false);
        }
      })
      .catch((e) => {
        console.error("Profile fetch error:", e);
        setIsLoading(false);
      });
  };

  return (
    <div className="wrapper-body px-4 py-2">

      {isLoading && (
        <div className="centered-loader">
          <ThreeDots
            height="100"
            width="100"
            radius="9"
            color="blue"
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClassName=""
            visible={true}
          />
        </div>
      )}
      {!isLoading && (
        // <form id="assign-asset">
        <div className="card card-radius">
          <div className="row">
            <div className="col-md-12">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={onSelectFile}
                accept="image/jpeg, image/jpg, image/png"
              />
              <ProfileHeaderCard
                data={{
                  name: userData?.personalDetail?.empName || "User",
                  role: userData?.personalDetail?.designation || "-",
                  department: userData?.personalDetail?.department || "-",
                  statusLabel: userData?.personalDetail?.status || "-",
                  email: userData?.personalDetail?.ccEmail || "-",
                  ccEmail: userData?.personalDetail?.ccEmail,
                  primaryEmail: userData?.personalDetail?.primaryEmail,
                  phone: employeePhone.display || "-",
                  location: userData?.personalDetail?.location || "-",
                  employeeId: userData?.personalDetail?.employeeId || "-",
                  employeeType: userData?.personalDetail?.employeeType || "-",
                  joinDate: formatCalendarDateDisplay(
                    userData?.personalDetail?.doj,
                  ),
                  reportingTo: userData?.personalDetail?.managerName || "-",
                  profileImage: profileImage,
                }}
                onAvatarClick={handleImageClick}
                onChangePassword={() => {
                  const modal = new window.bootstrap.Modal(
                    document.getElementById("staticBackdropChangePassword"),
                  );
                  modal.show();
                }}
              />
            </div>
          </div>

          <div className="row mt-5">
            <div className="container">
              <ul className="nav nav-tabs" id="myTabs" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    id="tab1-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab1"
                    type="button"
                    role="tab"
                    aria-controls="tab1"
                    aria-selected="true"
                  >
                    General Information
                  </button>
                </li>
                {/* <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab2-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab2"
                      type="button"
                      role="tab"
                      aria-controls="tab2"
                      aria-selected="false"
                    >
                      Job
                    </button>
                  </li> */}
                {/* <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab3-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab3"
                      type="button"
                      role="tab"
                      aria-controls="tab3"
                      aria-selected="false"
                    >
                      Leave
                    </button>
                  </li> */}
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="tab4-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab4"
                    type="button"
                    role="tab"
                    aria-controls="tab4"
                    aria-selected="false"
                  >
                    Assigned Assets
                  </button>
                </li>
                {showBankDetailsTab ? (
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab13-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab13"
                      type="button"
                      role="tab"
                      aria-controls="tab13"
                      aria-selected="false"
                    >
                      Bank Details
                    </button>
                  </li>
                ) : null}
                {/* <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab5-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab5"
                      type="button"
                      role="tab"
                      aria-controls="tab5"
                      aria-selected="false"
                    >
                      Performance
                    </button>
                  </li> */}
                {/* <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab6-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab6"
                      type="button"
                      role="tab"
                      aria-controls="tab6"
                      aria-selected="false"
                    >
                      Permission
                    </button>
                  </li> */}
                {/* <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab7-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab7"
                      type="button"
                      role="tab"
                      aria-controls="tab7"
                      aria-selected="false"
                    >
                      Documents
                    </button>
                  </li> */}
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="tab8-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab8"
                    type="button"
                    role="tab"
                    aria-controls="tab8"
                    aria-selected="false"
                  >
                    Salary Records
                  </button>
                </li>
                {/* <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab9-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab9"
                      type="button"
                      role="tab"
                      aria-controls="tab9"
                      aria-selected="false"
                    >
                      Organization Chart
                    </button>
                  </li> */}
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="tab10-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab10"
                    type="button"
                    role="tab"
                    aria-controls="tab10"
                    aria-selected="false"
                  >
                    Uploaded Documents
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="tab11-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab11"
                    type="button"
                    role="tab"
                    aria-controls="tab11"
                    aria-selected="false"
                  >
                    My Appraisals
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="tab12-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab12"
                    type="button"
                    role="tab"
                    aria-controls="tab12"
                    aria-selected="false"
                  >
                    Tax Documents
                  </button>
                </li>
                {showInsuranceDetailsTab ? <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="tab14-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab14"
                    type="button"
                    role="tab"
                    aria-controls="tab14"
                    aria-selected="false"
                  >
                    Insurance Details
                  </button>
                </li> : null}
              </ul>

              <div
                className="tab-content admin-profile-tab-content"
                id="myTabsContent"
              >
                <div
                  className="tab-pane fade show active mt-3"
                  id="tab1"
                  role="tabpanel"
                  aria-labelledby="tab1-tab"
                >
                  <div className="accordion" id="accordion1">
                    <div className="accordion-item rounded-lg-top">
                      <h2 className="accordion-header " id="heading1">
                        <button
                          className="accordion-button collapsed rounded-lg-top"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse1"
                          aria-expanded="false"
                          aria-controls="collapse1"
                        >
                          Basic info
                        </button>
                      </h2>
                      <div
                        id="collapse1"
                        className="accordion-collapse collapse"
                        aria-labelledby="heading1"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <div className="row mb-1 ">
                            <div className="col-md-3 text-black-50">
                              First Name
                            </div>
                            <div className="col-md-3 mb-2">
                              {userData?.personalDetail?.firstName
                                ? userData.personalDetail.firstName
                                  .charAt(0)
                                  .toUpperCase() +
                                userData.personalDetail.firstName
                                  .slice(1)
                                  .toLowerCase()
                                : "-"}
                            </div>
                            <div className="col-md-3 text-black-50">
                              Middle Name
                            </div>
                            <div className="col-md-3 mb-2">
                              {userData?.personalDetail?.middleName
                                ? userData.personalDetail.middleName
                                  .charAt(0)
                                  .toUpperCase() +
                                userData.personalDetail.middleName
                                  .slice(1)
                                  .toLowerCase()
                                : "-"}
                            </div>
                          </div>

                          <div className="row mb-1">
                            <div className="col-md-3 text-black-50">
                              Last Name
                            </div>
                            <div className="col-md-3 mb-2">
                              {userData?.personalDetail?.lastName
                                ? userData.personalDetail.lastName
                                  .charAt(0)
                                  .toUpperCase() +
                                userData.personalDetail.lastName
                                  .slice(1)
                                  .toLowerCase()
                                : "-"}
                            </div>
                            <div className="col-md-3 text-black-50">
                               Email Address
                            </div>
                            <div className="col-md-3 mb-2 text-break">
                              {profileEmail}
                            </div>
                          </div>

                          <div className="row mb-1">
                            <div className="col-md-3 text-black-50">
                              Employee Id
                            </div>
                            <div className="col-md-3 mb-2">
                              {userData?.personalDetail?.employeeId}
                            </div>
                            <div className="col-md-3 text-black-50">
                              Company Name
                            </div>
                            <div className="col-md-3 mb-2">
                              {companyNames.options.find(
                                (option) =>
                                  option.value ===
                                  userData?.personalDetail?.location,
                              )?.label ||
                                userData?.personalDetail?.location ||
                                "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="heading2">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse2"
                          aria-expanded="false"
                          aria-controls="collapse2"
                        >
                          Work
                        </button>
                      </h2>
                      <div
                        id="collapse2"
                        className="accordion-collapse collapse"
                        aria-labelledby="heading2"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <div className="row mb-1">
                            <div className="col-md-3 text-black-50">
                              Department
                            </div>
                            <div className="col-md-3 mb-2">
                              {userData?.personalDetail?.department
                                ? userData?.personalDetail?.department
                                : "-"}
                            </div>
                            <div className="col-md-3 text-black-50">
                              Date of Joining
                            </div>
                            <div className="col-md-3 mb-2">
                              {formatCalendarDateDisplay(
                                userData?.personalDetail?.doj,
                              )}
                            </div>
                          </div>

                          <div className="row mb-1">
                            <div className="col-md-3 text-black-50">
                              Confirmation Date
                            </div>
                            <div className="col-md-3 mb-2">
                              {formatCalendarDateDisplay(
                                getFirstDefinedField(
                                  userData?.personalDetail,
                                  "confirmationDate",
                                  "comconfirmationDate",
                                ),
                              )}
                            </div>
                           <div className="col-md-3 text-black-50">
                              Designation
                            </div>
                            <div className="col-md-3 mb-2">
                              {userData?.personalDetail?.designation
                                ? userData?.personalDetail?.designation
                                : "-"}
                            </div>
                          </div>

                          <div className="row mb-1">
                          
                            <div className="col-md-3 text-black-50">
                              Employee Type
                            </div>
                            <div className="col-md-3 mb-2">
                              {employeeTypeOptions.find(
                                (opt) =>
                                  opt.value ===
                                  userData?.personalDetail?.employeeType,
                              )?.label || "-"}
                            </div>
                            {userData?.personalDetail?.employeeType
                              ?.trim()
                              .toUpperCase() === "REGULAR" && (
                              <>
                                <div className="col-md-3 text-black-50">
                                  Employee Sub Type
                                </div>
                                <div className="col-md-3 mb-2">
                                  {userData?.personalDetail?.employeeSubType
                                    ? userData.personalDetail.employeeSubType.replace(
                                      "Fulltime",
                                      "Full Time",
                                    )
                                    : "-"}
                                </div>
                              </>
                            )}
                          </div>

                          <div className="row mb-1">
                            <div className="col-md-3 text-black-50">
                              Reporting Manager
                            </div>
                            <div className="col-md-3 mb-2">
                              {userData?.personalDetail?.managerName
                                ? userData?.personalDetail?.managerName
                                : "-"}
                            </div>
                          <div className="col-md-3 text-black-50">
                              Leave Manager
                            </div>
                            <div className="col-md-3 mb-2">
                              {getLeaveManagerDisplayName(
                                userData?.personalDetail,
                                userData,
                              )}
                            </div>
                          </div>

                          <div className="row mb-1">
                          
                               <div className="col-md-3 text-black-50">
                              Current Location
                            </div>
                            <div className="col-md-3 mb-2">
                              
                                {userData?.personalDetail?.currentLocation
                                ? userData?.personalDetail?.currentLocation
                                : "-"}
                              
                            </div>
 <div className="col-md-3 text-black-50">
                              Joining Location
                            </div>
                            <div className="col-md-3 mb-2">
                              
                                {userData?.personalDetail?.joiningLocation
                                ? userData?.personalDetail?.joiningLocation
                                : "-"}
                              
                            </div>
                          </div>

                          {isSqeLabs && isSQEEmployeeType(userData?.personalDetail?.employeeType ?? userData?.personalDetail?.empType) && userData?.personalDetail?.id && (
                            <SqeShiftTiming employeeId={userData.personalDetail.id} readEndpoint={API_MY_SQE_SHIFT_TIMING} readOnly />
                          )}
                          
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2
                        className="accordion-header"
                        id="headingTechnicalStack"
                      >
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseTechnicalStack"
                          aria-expanded="false"
                          aria-controls="collapseTechnicalStack"
                        >
                          Technical Skills
                        </button>
                      </h2>
                      <div
                        id="collapseTechnicalStack"
                        className="accordion-collapse collapse"
                        aria-labelledby="headingTechnicalStack"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <EmployeeTechStack
                            employeeId={userData?.personalDetail?.id}
                            accessToken={accessToken}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2
                        className="accordion-header"
                        id="headingCertifications"
                      >
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseCertifications"
                          aria-expanded="false"
                          aria-controls="collapseCertifications"
                        >
                          Certifications
                        </button>
                      </h2>
                      <div
                        id="collapseCertifications"
                        className="accordion-collapse collapse"
                        aria-labelledby="headingCertifications"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <EmployeeCertifications accessToken={accessToken} />
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="heading3">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse3"
                          aria-expanded="false"
                          aria-controls="collapse3"
                        >
                          Personal Details
                        </button>
                      </h2>
                      <div
                        id="collapse3"
                        className="accordion-collapse collapse"
                        aria-labelledby="heading3"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <div className="row">
                            <div className="row mb-3 ">
                              <div className="col-md-3 text-black-50">
                                {isUsEmployee ? "SSN (Social Security Number)" : "Aadhar Number"}
                              </div>
                              <div className="col-md-3">
                                {(isUsEmployee ? userData?.personalDetail?.ssn : userData?.personalDetail?.aadharNum)
                                  ? (isUsEmployee ? userData?.personalDetail?.ssn : userData?.personalDetail?.aadharNum)
                                  : "-"}
                              </div>
                              <div className="col-md-3 text-black-50">
                                Date of Birth
                              </div>
                              <div className="col-md-3">
                                {formatCalendarDateDisplay(
                                  userData?.personalDetail?.dob,
                                )}
                              </div>
                            </div>
                            <div className="row mb-3 ">
                              <div className="col-md-3 text-black-50">
                                Passport Number
                              </div>
                              <div className="col-md-3">
                                {userData?.personalDetail?.passportNum
                                  ? userData?.personalDetail?.passportNum
                                  : "-"}
                              </div>
                              {!isUsEmployee && (
                                <>
                                  <div className="col-md-3 text-black-50">
                                    Blood Group
                                  </div>
                                  <div className="col-md-3">
                                    {userData?.personalDetail?.bloodGroup
                                      ? userData?.personalDetail?.bloodGroup
                                      : "-"}
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="row mb-3 ">
                              <div className="col-md-3 text-black-50">
                                {isUsEmployee ? "ZIP Code" : "PAN Card Number"}
                              </div>
                              <div className="col-md-3">
                                {(isUsEmployee ? userData?.personalDetail?.zipCode : userData?.personalDetail?.panNum)
                                  ? (isUsEmployee ? userData?.personalDetail?.zipCode : userData?.personalDetail?.panNum)
                                  : "-"}
                              </div>
                              <div className="col-md-3 text-black-50">
                                {" "}
                                Gender
                              </div>
                              <div className="col-md-3">
                                {userData?.personalDetail?.gender
                                  ? userData.personalDetail.gender
                                    .charAt(0)
                                    .toUpperCase() +
                                  userData.personalDetail.gender.slice(1)
                                  : "-"}
                              </div>
                            </div>
                            <div className="row mb-3 ">
                              <div className="col-md-3 text-black-50">
                                Emergency Contact Number
                              </div>
                              <div className="col-md-3">
                                {userData?.personalDetail?.alternateNumber
                                  ? userData?.personalDetail?.alternateNumber
                                  : "-"}
                              </div>
                              <div className="col-md-3 text-black-50">
                                {" "}
                                Marital Status
                              </div>
                              <div className="col-md-3">
                                {userData?.personalDetail?.maritalStatus
                                  ? userData?.personalDetail?.maritalStatus
                                  : "-"}
                              </div>
                            </div>
                            <div className="row mb-3 ">
                              <div className="col-md-3 text-black-50">
                                Emergency Contact Name
                              </div>
                              <div className="col-md-3">
                                {userData?.personalDetail?.alternateName
                                  ? userData?.personalDetail?.alternateName
                                  : "-"}
                              </div>
                              <div className="col-md-3 text-black-50">
                                {" "}
                                Current Residential Address
                              </div>
                              <div className="col-md-3">
                                {userData?.personalDetail?.presentAddress
                                  ? userData?.personalDetail?.presentAddress
                                  : "-"}
                              </div>
                            </div>
                            <div className="row mb-3 ">
                              <div className="col-md-3 text-black-50">
                                Relationship with Emergency Contact Person
                              </div>
                              <div className="col-md-3">
                                {userData?.personalDetail
                                  ?.relationWithAlternateNo
                                  ? userData?.personalDetail
                                    ?.relationWithAlternateNo
                                  : "-"}
                              </div>
                              <div className="col-md-3 text-black-50">
                                {" "}
                                Permanent Address
                              </div>
                              <div className="col-md-3">
                                {userData?.personalDetail?.permanentAddress
                                  ? userData?.personalDetail?.permanentAddress
                                  : "-"}
                              </div>
                            </div>
                            <div className="row mb-3 ">
                                <div className="col-md-3 text-black-50">
                               Personal Email Address
                              </div>
                              <div className="col-md-3">
                                {userData?.personalDetail?.personalEmail
                                  ? userData?.personalDetail?.personalEmail
                                  : "-"}
                              </div>
                              
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="heading4">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse4"
                          aria-expanded="false"
                          aria-controls="collapse4"
                        >
                          Work Experience
                        </button>
                      </h2>
                      <div
                        id="collapse4"
                        className="accordion-collapse collapse"
                        aria-labelledby="heading4"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <div className="row">
                            <div className="col-md-12">
                              <WorkExperience workData={userData?.experience} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="heading5">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse5"
                          aria-expanded="false"
                          aria-controls="collapse5"
                        >
                          Education
                        </button>
                      </h2>
                      <div
                        id="collapse5"
                        className="accordion-collapse collapse"
                        aria-labelledby="heading5"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <div className="row">
                            <div className="col-md-12">
                              <Education educationData={userData?.education} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item  last-accordion-item rounded-lg-bottom">
                      <h2 className="accordion-header" id="heading6">
                        <button
                          className="accordion-button collapsed rounded-lg-bottom"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse6"
                          aria-expanded="false"
                          aria-controls="collapse6"
                        >
                          Dependents
                        </button>
                      </h2>
                      <div
                        id="collapse6"
                        className="accordion-collapse collapse"
                        aria-labelledby="heading6"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <div className="row">
                            <div className="col-md-12">
                              <Dependents
                                dependentData={userData?.dependents}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="tab-pane fade"
                  id="tab2"
                  role="tabpanel"
                  aria-labelledby="tab2-tab"
                ></div>

                <div
                  className="tab-pane fade"
                  id="tab3"
                  role="tabpanel"
                  aria-labelledby="tab3-tab"
                ></div>

                <div
                  className="tab-pane fade"
                  id="tab4"
                  role="tabpanel"
                  aria-labelledby="tab4-tab"
                >
                  <EmployeeAsset assestData={userData?.assestDetail} />
                </div>

                {showBankDetailsTab ? (
                  <div
                    className="tab-pane fade"
                    id="tab13"
                    role="tabpanel"
                    aria-labelledby="tab13-tab"
                  >
                    <ProfileBankDetails
                      employeeId={userData?.personalDetail?.id}
                      accessToken={accessToken}
                    />
                  </div>
                ) : null}

                <div
                  className="tab-pane fade"
                  id="tab5"
                  role="tabpanel"
                  aria-labelledby="tab5-tab"
                >
                  <OrgChartExample />
                </div>

                <div
                  className="tab-pane fade"
                  id="tab6"
                  role="tabpanel"
                  aria-labelledby="tab6-tab"
                ></div>

                <div
                  className="tab-pane fade"
                  id="tab7"
                  role="tabpanel"
                  aria-labelledby="tab7-tab"
                ></div>

                <div
                  className="tab-pane fade"
                  id="tab8"
                  role="tabpanel"
                  aria-labelledby="tab8-tab"
                >
                  <Salary
                    sqeOnly2026={
                      isSqeLabs &&
                      isSQEEmployeeType(
                        userData?.personalDetail?.employeeType ??
                          userData?.personalDetail?.empType,
                      )
                    }
                  />
                </div>
                {/* <div
                  className="tab-pane fade"
                  id="tab9"
                  role="tabpanel"
                  aria-labelledby="tab9-tab"
                >
                  <IndexChart />
                </div> */}
                <div
                  className="tab-pane fade"
                  id="tab10"
                  role="tabpanel"
                  aria-labelledby="tab10-tab"
                >
                  <DocumentList />
                </div>
                <div
                  className="tab-pane fade"
                  id="tab11"
                  role="tabpanel"
                  aria-labelledby="tab11-tab"
                >
                  <AppraisalList />
                </div>

                <div
                  className="tab-pane fade"
                  id="tab12"
                  role="tabpanel"
                  aria-labelledby="tab12-tab"
                >
                  <Form16 />
                </div>
                {showInsuranceDetailsTab ? <div
                  className="tab-pane fade"
                  id="tab14"
                  role="tabpanel"
                  aria-labelledby="tab14-tab"
                >
                  <ProfileInsuranceDetails
                    userData={userData}
                    insuranceDetails={insuranceDetails}
                  />
                </div> : null}
              </div>
            </div>
          </div>
        </div>
        // </form>
      )}
      <div
        className="modal fade"
        id="staticBackdropChangePassword"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex="-1"
        aria-labelledby="staticBackdropLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <ChangePassword />
          </div>
        </div>
      </div>
      <Footer />

      {showCropModal && <div className="modal-backdrop fade show"></div>}
      <div
        className={`modal fade ${showCropModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content p-3">
            <div className="modal-header p-0 pb-2">
              <h5 className="modal-title">Crop Photo</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowCropModal(false)}
              ></button>
            </div>
            <div className="modal-body text-center">
              {isPreviewMode ? (
                <img
                  src={previewImage}
                  alt="Preview"
                  className="rounded-circle preview-image"
                  width="200"
                  height="200"
                />
              ) : (
                upImg && (
                  <div
                    style={{ position: "relative", width: "100%", height: 400 }}
                  >
                    <Cropper
                      image={upImg}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="round"
                      showGrid={false}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                      onMediaLoaded={onMediaLoaded}
                      cropSize={{ width: 200, height: 200 }}
                      minZoom={minZoom}
                      maxZoom={3}
                    />
                  </div>
                )
              )}
            </div>

            <div className="modal-footer gap-2">
              {isPreviewMode ? (
                <>
                  <button
                    className=" theme-button bg-grey"
                    onClick={() => setIsPreviewMode(false)}
                    disabled={isUploading}
                  >
                    Back
                  </button>
                  <button
                    className=" theme-button"
                    onClick={handleCropSave}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <ThreeDots
                        height="20"
                        width="40"
                        radius="9"
                        color="white"
                        ariaLabel="three-dots-loading"
                        visible={true}
                      />
                    ) : (
                      "Update"
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn theme-button"
                    onClick={handlePreviewCrop}
                    disabled={isUploading}
                  >
                    Preview
                  </button>
                  <button
                    className="btn theme-button"
                    onClick={handleCropSave}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <ThreeDots
                        height="20"
                        width="40"
                        radius="9"
                        color="white"
                        ariaLabel="three-dots-loading"
                        visible={true}
                      />
                    ) : (
                      "Update"
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;
